import { BoxWidgetData, IBoxWidgetData, IBoxWidgetStyle, ILinkData } from "@pageBuilder/typings";
import { BackgroundDataRenderer } from "../../../shared/rendering/background-data-renderer";
import { CssProperties } from "../../../models/css-property";
import { WidgetRendererBase } from "../widget-renderer-base";
import { VerticalAlignmentRenderer } from "../vertical-alignment-renderer";
import { BoxScriptRenderer } from "./box-script.renderer";
import { IRenderingCommonContext } from "@commonRendering/types";
import { ObjectUtil } from "src/utils/object-util";
import { LinkRenderer } from "../link.renderer";

export class BoxWidgetRenderer extends WidgetRendererBase<IBoxWidgetData> {

    private readonly backgroundRenderer: BackgroundDataRenderer;
    private readonly verticalAlignmentRenderer: VerticalAlignmentRenderer;
    private readonly scriptRenderer: BoxScriptRenderer;
    private readonly linkRenderer: LinkRenderer;

    private readonly backgroundRegularSelector = ".widget-contents > .widget-content.regular > .widget-background";
    private readonly backgroundHoverSelector = ".widget-contents > .widget-content.hover > .widget-background";

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.backgroundRenderer = new BackgroundDataRenderer(this.styleService);
        this.verticalAlignmentRenderer = new VerticalAlignmentRenderer();
        this.scriptRenderer = new BoxScriptRenderer();
        this.linkRenderer = new LinkRenderer();
    }

    protected createNewInternal(): HTMLElement {
        const regularContent = this.createContentElement();
        regularContent.classList.add("regular");

        const widgetContents = document.createElement("div");
        widgetContents.classList.add("widget-contents");
        widgetContents.append(regularContent);

        const element = this.createWidgetElement("boxWidget", widgetContents);
        element.classList.add("empty");

        return element;
    }

    protected override createContentElement(): HTMLElement {
        const content = super.createContentElement();
        const background = document.createElement("div");
        background.classList.add("widget-background");
        const widgetArea = document.createElement("div");
        widgetArea.classList.add("widgets-area");
        widgetArea.setAttribute("data-widgets-area", "");
        content.append(background, widgetArea);

        return content;
    }

    protected renderInternal(element: HTMLElement, data: IBoxWidgetData): HTMLElement {
        this.renderLink(element, data.linkData);
        this.renderStyles(element, data);
        this.renderWidgetState(element, data);

        this.backgroundRenderer.renderBackground(element, data.regularStateData.background, this.backgroundRegularSelector, "box widget regular background");

        if (data.hoverStateType !== "none") {
            this.backgroundRenderer.renderBackground(element, data.hoverStateData.background, this.backgroundHoverSelector, "box widget hovered background");
        }

        this.renderScript(element, data);
        return element;
    }

    private renderLink(element: HTMLElement, data: ILinkData | undefined): void {
        if (!data || data.type === "none") {
            return;
        }

        const content = element.querySelector(".widget-contents") as HTMLElement;
        let link = element.querySelector<HTMLAnchorElement>("a");
        if (!link) {
            link = document.createElement("a");
            content.appendChild(link);
        }

        if(!this.isEditorRendering) {
            link.classList.add("box-link-active");
        }

        this.linkRenderer.render(link, data);
    }

    protected renderStyles(element: HTMLElement, data: IBoxWidgetData): void {
        if (data.hoverStateType === "none") {
            BoxWidgetData.updateWithDefaultsHover(data);
        }

        this.cleanupStyles(element);
        if (!data.regularStateData.style) {
            return;
        }

        if (data.hoverStateType !== "hover") {
            this.renderBaseStyles(element, data.regularStateData.style);
        }

        if (data.isStateDuplicated) {
            data.hoverStateData = ObjectUtil.deepClone(data.regularStateData);
        }

        this.renderContentStyles(element, data.regularStateData.style, data.hoverStateData.style);
        this.renderBackgroundStyles(element, data.regularStateData.style, data.hoverStateData.style);
    }

    private renderBaseStyles(element: HTMLElement, style?: IBoxWidgetStyle): void {
        if (style) {
            element.style.height = style.heightData?.height ? `${style.heightData.height}px` : "auto";
        }

        this.animationRenderer.setAnimationStyles(element, style?.animation);
    }

    private renderContentStyles(element: HTMLElement, regularStyle: IBoxWidgetStyle, hoverStyle: IBoxWidgetStyle): void {
        const contentRegularProps = new CssProperties();

        if (regularStyle?.borderRadius) {
            contentRegularProps.tryAdd("border-top-left-radius", `${regularStyle.borderRadius.topLeftPx}px`);
            contentRegularProps.tryAdd("border-top-right-radius", `${regularStyle.borderRadius.topRightPx}px`);
            contentRegularProps.tryAdd("border-bottom-left-radius", `${regularStyle.borderRadius.bottomLeftPx}px`);
            contentRegularProps.tryAdd("border-bottom-right-radius", `${regularStyle.borderRadius.bottomRightPx}px`);
        }

        if (regularStyle?.displayShadow) {
            contentRegularProps.tryAdd("box-shadow", `0px 5px 22px ${regularStyle.shadowColor}`);
        }

        contentRegularProps.tryAdd("border-style", "solid");
        if (regularStyle?.thickness) {
            contentRegularProps.tryAdd("border-width", `${regularStyle.thickness}px`);
        }

        if (regularStyle?.borderColor) {
            contentRegularProps.tryAdd("border-color", `${regularStyle.borderColor}`);
        }

        if (regularStyle?.padding) {
            contentRegularProps.tryAdd("padding", `${regularStyle.padding}px`);
        }

        if (regularStyle.verticalAlignment) {
            const alignItems = this.verticalAlignmentRenderer.getAlignmentBySelfPosition(regularStyle.verticalAlignment);

            contentRegularProps.tryAdd("align-items", alignItems);
        }

        if (contentRegularProps.data.length > 0) {
            this.styleService.setStyle(element, `#${element.id} > .widget-contents > .widget-content.regular`, contentRegularProps.data);
        }

        const contentHoverProps = new CssProperties();

        if (hoverStyle?.borderRadius) {
            contentHoverProps.tryAdd("border-top-left-radius", `${hoverStyle.borderRadius.topLeftPx}px`);
            contentHoverProps.tryAdd("border-top-right-radius", `${hoverStyle.borderRadius.topRightPx}px`);
            contentHoverProps.tryAdd("border-bottom-left-radius", `${hoverStyle.borderRadius.bottomLeftPx}px`);
            contentHoverProps.tryAdd("border-bottom-right-radius", `${hoverStyle.borderRadius.bottomRightPx}px`);
        }

        if (hoverStyle?.displayShadow) {
            contentHoverProps.tryAdd("box-shadow", `0px 5px 22px ${hoverStyle.shadowColor}`);
        }

        contentHoverProps.tryAdd("border-style", "solid");
        if (hoverStyle?.thickness) {
            contentHoverProps.tryAdd("border-width", `${hoverStyle.thickness}px`);
        }

        if (hoverStyle?.borderColor) {
            contentHoverProps.tryAdd("border-color", `${hoverStyle.borderColor}`);
        }

        if (hoverStyle?.padding) {
            contentHoverProps.tryAdd("padding", `${hoverStyle.padding}px`);
        }

        if (hoverStyle.verticalAlignment) {
            const alignItems = this.verticalAlignmentRenderer.getAlignmentBySelfPosition(hoverStyle.verticalAlignment);

            contentHoverProps.tryAdd("align-items", alignItems);
        }

        if (contentHoverProps.data.length > 0) {
            this.styleService.setStyle(element, `#${element.id} > .widget-contents > .widget-content.hover`, contentHoverProps.data);
        }

    }

    private renderBackgroundStyles(element: HTMLElement, regularStyle: IBoxWidgetStyle, hoverStyle: IBoxWidgetStyle): void {
        const borderRegularProps = new CssProperties();

        if (regularStyle?.thickness) {
            borderRegularProps.tryAdd("margin", `-${regularStyle.thickness}px`);
        }

        borderRegularProps.tryAdd("border-style", "solid");
        if (regularStyle?.thickness) {
            borderRegularProps.tryAdd("border-width", `${regularStyle.thickness}px`);
        }

        if (regularStyle?.borderColor) {
            borderRegularProps.tryAdd("border-color", `${regularStyle.borderColor}`);
        }

        this.styleService.setStyle(element, `#${element.id} > ${this.backgroundRegularSelector}`, borderRegularProps.data);

        const borderHoverProps = new CssProperties();

        if (hoverStyle?.thickness) {
            borderHoverProps.tryAdd("margin", `-${hoverStyle.thickness}px`);
        }

        borderHoverProps.tryAdd("border-style", "solid");
        if (hoverStyle?.thickness) {
            borderHoverProps.tryAdd("border-width", `${hoverStyle.thickness}px`);
        }

        if (hoverStyle?.borderColor) {
            borderHoverProps.tryAdd("border-color", `${hoverStyle.borderColor}`);
        }

        this.styleService.setStyle(element, `#${element.id} > ${this.backgroundHoverSelector}`, borderHoverProps.data);
    }

    private renderWidgetState(element: HTMLElement, data: IBoxWidgetData): void {
        if (this.isEditorRendering) {
            element.dataset.editorMode = "";
        }

        element.dataset.hoverState = data.hoverStateType;

        const widgetContents = element.querySelector<HTMLElement>(".widget-contents");
        let contentHover = element.querySelector<HTMLElement>(".widget-content.hover");

        if (data.hoverStateType === "none") {
            contentHover?.remove();
        } else {
            if (contentHover) {
                return;
            }

            contentHover = this.createContentElement();
            contentHover.classList.add("hover");
            widgetContents?.append(contentHover);
        }
    }

    private renderScript(element: HTMLElement, data: IBoxWidgetData): void {
        this.scriptRenderer.renderCommon(this.renderedDocument);
        const existingData = this.getData(element) as IBoxWidgetData;
        if (!BoxWidgetData.localScriptRerenderNeeded(existingData, data)) {
            this.scriptRenderer.renderLocal(element);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        //nothing
    }

    private getBaseElementSelectors(element: HTMLElement): string [] {
        return [
            `#${element.id}`,
            `#${element.id} > .widget-contents > .widget-content.regular`,
            `#${element.id} > .widget-contents > .widget-content.hover`,
            `#${element.id} > ${this.backgroundRegularSelector}`,
            `#${element.id} > ${this.backgroundHoverSelector}`
        ];
    }

    protected getContentSelectorsInternal(element: HTMLElement): string[] {
        const selectors = this.getBaseElementSelectors(element);
        selectors.push(
            ...this.backgroundRenderer.getContentSelectorsInternal(element, this.backgroundRegularSelector),
            ...this.backgroundRenderer.getContentSelectorsInternal(element, this.backgroundHoverSelector)
        );
        return selectors;
    }

    private cleanupStyles(element: HTMLElement): void {
        this.getBaseElementSelectors(element).forEach(selector => this.styleService.clearStyle(element, selector));
    }

}
